import { ArrowDown, Gamepad2, Wallet, X } from 'lucide-react'
import { type SVGProps } from 'react'

import {
  DiscordIcon,
  TrophyOutlineIcon,
  TwitchIcon,
} from '@/components/icons/BrandIcons'
import { SectionLabel } from '@/features/landing/components/SectionLabel'
import { Wordmark } from '@/features/landing/components/Wordmark'

type IconCmp = (props: SVGProps<SVGSVGElement>) => JSX.Element

/**
 * "Stop juggling a stack of tabs" — typographic comparison. Brand glyphs for
 * Discord and Twitch are the real Simple-Icons paths; Challonge has no
 * widely-recognised logo so a generic trophy is used in its place. Join It
 * gets a wallet glyph, and Steam/Epic/Battle.net share a generic gamepad
 * glyph (no canonical brand marks we can rely on) — they're where a squad's
 * gamertags scatter, which is exactly what e404's linked accounts pull back
 * together.
 */
const FRAGMENTED: { name: string; purpose: string; icon: IconCmp; rotate: string }[] = [
  { name: 'Discord', purpose: 'voice & chat', icon: DiscordIcon, rotate: '-rotate-2' },
  { name: 'Challonge', purpose: 'brackets', icon: TrophyOutlineIcon, rotate: 'rotate-1' },
  { name: 'Twitch', purpose: 'streaming', icon: TwitchIcon, rotate: '-rotate-1' },
  { name: 'Join It', purpose: 'dues & roster', icon: Wallet as unknown as IconCmp, rotate: 'rotate-2' },
  { name: 'Steam', purpose: 'gamertags', icon: Gamepad2 as unknown as IconCmp, rotate: '-rotate-1' },
  { name: 'Epic', purpose: 'gamertags', icon: Gamepad2 as unknown as IconCmp, rotate: 'rotate-2' },
  { name: 'Battle.net', purpose: 'battletags', icon: Gamepad2 as unknown as IconCmp, rotate: '-rotate-2' },
] as const

const UNIFIED_FACETS = [
  'tournaments',
  'chat',
  'stream',
  'dues',
  'roster',
  'voice',
] as const

export function Comparison() {
  return (
    <section className="relative border-b border-border/60 px-6 py-24 md:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-col items-start gap-6 md:mb-16">
          <SectionLabel index="02" label="The 11pm problem" />
          <h2 className="font-display max-w-3xl text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
            Right now your clan lives in <span className="gradient-text">five Discord threads</span> and a
            spreadsheet.
          </h2>
          <p className="max-w-2xl text-base text-fg-muted text-pretty md:text-lg">
            It’s 11pm. You’re chasing a headcount in one tab, the tournament in another, the dues in a third, and half the squad never sees the message. e404 pulls the roster, events, streams and Discord into one place. You stop being the group’s admin and get back to playing.
          </p>
        </div>

        <div className="grid items-center gap-10 md:grid-cols-[1fr_auto_1fr]">
          {/* Fragmented column — chaotic stack of disconnected tabs */}
          <div className="relative">
            <p className="mb-6 font-display text-xs tracking-[0.25em] text-fg-subtle uppercase">
              Before · seven tabs
            </p>
            <ul className="flex flex-col gap-3">
              {FRAGMENTED.map((service) => (
                <li
                  key={service.name}
                  className={`group flex items-center justify-between rounded-lg border border-border bg-surface px-4 py-3 transition-transform hover:translate-x-1 ${service.rotate}`}
                >
                  <div className="flex items-center gap-3">
                    <service.icon
                      className="size-4 text-fg-subtle"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                    <span className="font-display text-base font-medium text-fg-muted line-through decoration-fg-subtle/60">
                      {service.name}
                    </span>
                    <span className="font-display text-xs tracking-wider text-fg-subtle uppercase">
                      {service.purpose}
                    </span>
                  </div>
                  <X
                    className="size-3.5 text-fg-subtle/60"
                    strokeWidth={1.5}
                    aria-hidden
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Connector — vertical arrow on md, horizontal-fade on sm */}
          <div className="flex items-center justify-center" aria-hidden>
            <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border-strong bg-surface md:h-14 md:w-14">
              <ArrowDown className="size-5 text-primary md:rotate-[-90deg]" />
            </div>
          </div>

          {/* Unified column — single e404 panel */}
          <div className="relative">
            <p className="mb-6 font-display text-xs tracking-[0.25em] text-fg-subtle uppercase md:text-right">
              After · one app
            </p>
            <div className="relative overflow-hidden rounded-xl border border-primary/30 bg-surface p-8 shadow-2xl shadow-primary/10">
              {/* Subtle gradient flash inside the panel */}
              <div
                aria-hidden
                className="absolute inset-0 -z-10 opacity-20"
                style={{ backgroundImage: 'var(--gradient-accent)' }}
              />
              <div className="flex flex-col items-center text-center">
                <Wordmark size="xl" className="mb-6" />
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
                  {UNIFIED_FACETS.map((facet, idx) => (
                    <span
                      key={facet}
                      className="font-display text-sm font-medium tracking-tight text-fg flex items-center gap-3"
                    >
                      {facet}
                      {idx < UNIFIED_FACETS.length - 1 && (
                        <span
                          aria-hidden
                          className="h-1 w-1 rounded-full bg-primary/60"
                        />
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="mt-16 text-center font-display text-2xl font-semibold tracking-tight text-fg-muted md:text-3xl">
          One place your squad opens.{' '}
          <span className="text-fg">Every night.</span>
        </p>
      </div>
    </section>
  )
}
